import React from 'react';
import { Typography, Box, Grid, Container, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CommonSection from '../components/CommonSection';
import CallToAction from '../components/CallToAction';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const About = ({ darkMode }) => {
  const points = [
    "Integrity: We adhere to the highest ethical standards and transparency in all our dealings.",
    "Client-Centric Approach: We listen, understand, and tailor solutions that best suit our clients' needs.",
    "Excellence: We are committed to delivering top-notch services that exceed expectations.",
  ];

  const textColor = darkMode ? '#ddd' : '#444';
  const backgroundColor = darkMode ? '#222' : '#f5f5f5';
  const highlightColor = darkMode ? '#fff' : '#000';

  return (
    <>
      <CommonSection title="About Us" />
      <Container>
        {/* About Us Section */}
        <Grid container spacing={4} sx={{ mt: 6 }}>
    {/* Left Column */}
<Grid 
  item 
  xs={12} 
  md={12} 
  data-aos="fade-up" 
  data-aos-delay="100"
  sx={{
    backgroundColor: "white",
    padding: 4,
    borderRadius: 2,
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  }}
>
  <Typography
    variant="body1"
    sx={{
      color: textColor,
      mb: 3,
      lineHeight: 1.8,
      fontSize: "1rem",
      textAlign: "justify",
    }}
  >
    Welcome to <strong>Fincal Business Solutions!</strong> We are a leading accounting firm committed to delivering expert 
    financial guidance and support to businesses of all sizes. With our personalized approach, we help streamline your 
    financial operations, ensuring compliance and accuracy in every aspect of accounting and taxation.
  </Typography>
  <List>
    {points.map((point, index) => (
      <ListItem
        key={index}
        data-aos="fade-up"
        data-aos-delay={`${150 * (index + 1)}`}
        sx={{
          backgroundColor: "#f9f9f9",
          marginBottom: 2,
          borderRadius: 1,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          padding: 2,
          "&:hover": {
            backgroundColor: "#fff6e0",
            transform: "translateY(-2px)",
            transition: "0.3s",
          },
        }}
      >
        <ListItemIcon>
          <CheckCircleIcon color="warning" fontSize="large" />
        </ListItemIcon>
        <ListItemText
          primary={point}
          primaryTypographyProps={{
            sx: {
              color: highlightColor,
              fontSize: "1.1rem",
              fontWeight: "500",
            },
          }}
        />
      </ListItem>
    ))}
  </List>
</Grid>

{/* Right Column */}
<Grid 
  item 
  xs={12} 
  md={12} 
  data-aos="fade-up" 
  data-aos-delay="200"
  sx={{
    backgroundColor: "#f9f9f9",
    padding: 4,
    borderRadius: 2,
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    marginTop: 4,
  }}
>
  <Typography
    variant="body1"
    sx={{
      color: textColor,
      mb: 3,
      lineHeight: 1.8,
      fontSize: "1rem",
      textAlign: "justify",
    }}
  >
    At <strong>Fincal Business Solutions</strong>, we believe in providing comprehensive financial management solutions that are 
    tailored to meet the unique needs of every client. With years of experience in accounting, tax services, and financial planning, 
    our team is equipped to help you navigate complex financial landscapes.
  </Typography>
</Grid>

        </Grid>

    {/* Core Values Section */}
<Box
  py={8}
  px={3}
  sx={{
    backgroundColor,
    color: highlightColor,
    textAlign: "center",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
  }}
>
  <Typography
    variant="h4"
    gutterBottom
    sx={{
      fontWeight: "bold",
      marginBottom: 4,
      color: "primary.main",
    }}
  >
    Our Core Values
  </Typography>
  <Grid container spacing={5} justifyContent="center">
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          padding: 3,
          borderRadius: "8px",
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          Innovation
        </Typography>
        <Typography variant="body2">
          We foster creativity and pursue new ideas to deliver transformative solutions.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          padding: 3,
          borderRadius: "8px",
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          Integrity
        </Typography>
        <Typography variant="body2">
          We conduct our business with honesty and transparency, ensuring trust in all our relationships.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          padding: 3,
          borderRadius: "8px",
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          Excellence
        </Typography>
        <Typography variant="body2">
          We strive for the highest standards in every project, aiming for flawless execution and exceptional results.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          padding: 3,
          borderRadius: "8px",
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          Collaboration
        </Typography>
        <Typography variant="body2">
          We believe in the strength of teamwork and value the diverse perspectives of our clients and partners.
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={6} md={4}>
      <Box
        sx={{
          padding: 3,
          borderRadius: "8px",
          backgroundColor: "white",
          color: "text.primary",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 1 }}>
          Customer Success
        </Typography>
        <Typography variant="body2">
          Our clients’ success is our success, and we are dedicated to ensuring they achieve their goals.
        </Typography>
      </Box>
    </Grid>
  </Grid>
</Box>

        {/* CEO Message Section */}
        <Box py={5} textAlign="center" sx={{ backgroundColor: darkMode ? '#333' : '#f9f9f9', color: highlightColor }}>
          <Typography variant="h4" gutterBottom>
            A Message from Our CEO
          </Typography>
          <Typography variant="body1" paragraph>
            "At our company, we believe in the power of innovation and collaboration. Our team is driven by passion, and we are committed
            to delivering exceptional solutions that exceed our clients' expectations. Our success comes from the trust and partnerships
            we've built over the years, and we look forward to continuing to push the boundaries of what's possible."
          </Typography>
        </Box>

        {/* Mission Section */}
        <Box py={5} textAlign="center" sx={{ backgroundColor, color: highlightColor }}>
          <Typography variant="h4" gutterBottom>
            Our Mission
          </Typography>
          <Typography variant="body1" paragraph>
            Our mission is to empower businesses by delivering innovative, high-quality solutions that drive growth, improve operational efficiency, and create a lasting impact.
          </Typography>
        </Box>

        {/* Vision Section */}
        <Box py={5} textAlign="center" sx={{ backgroundColor, color: highlightColor }}>
          <Typography variant="h4" gutterBottom>
            Our Vision
          </Typography>
          <Typography variant="body1" paragraph>
            To be a global leader in digital solutions, renowned for transforming ideas into reality and delivering sustainable results.
          </Typography>
        </Box>

        <CallToAction darkMode={darkMode} />
      </Container>
    </>
  );
};

export default About;
